import { Fragment, useContext, useState } from "react";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import Heading from "../UI/Heading";
import DataContext from "../../context/data-context";
import { FaEdit } from "react-icons/fa";
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/uploadFile";
import { AuthContext } from "../../context/auth-context";
import { requestPartnerAPI } from "../../api/server";
import { uploadBase64String } from "../../util/uploadBase64String";
import { MdCheck, MdClose } from "react-icons/md";
import { isoDateToString } from "../../util/format-date";
import { UtilityContext } from "../../context/util-context";

export default function CreditApplication() {
  const { user } = useContext(AuthContext);
  const { partner, fetchData } = useContext(DataContext);
  const { navigate } = useContext(UtilityContext);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState();
  const [edit, setEdit] = useState(false);

  // =================================================== SUBMIT HANDLER
  const submitHandler = async () => {
    try {
      const data = {
        driversLicenseNumber:
          document.getElementById("driversLicenseNumber").value || false,
        phone: document.getElementById("phone").value || false,
        ssn: document.getElementById("ssn").value || false,
        residenceType: document.getElementById("residenceType").value || false,
        monthlyRent: document.getElementById("monthlyRent").value || false,
        yearsAtResidence:
          document.getElementById("yearsAtResidence").value || false,
        monthsAtResidence:
          document.getElementById("monthsAtResidence").value || false,
        employer: document.getElementById("employer").value || false,
        occupation: document.getElementById("occupation").value || false,
        employmentYears:
          document.getElementById("employmentYears").value || false,
        monthlySalary: document.getElementById("monthlySalary").value || false,
        otherIncome: document.getElementById("otherIncome").value || false,
      };

      const files = {
        taxReturns: document.getElementById("taxReturns").files,
        payStubs: document.getElementById("payStubs").files,
        bankStatements: document.getElementById("bankStatements").files,
        ssnFront: document.getElementById("ssnFront").files[0],
        proofOfResidence:
          document.getElementById("proofOfResidence").files[0] || false,
        selfieId: document.getElementById("selfieId").files[0] || false,
      };

      setLoading(true);
      // Upload Files
      await Promise.all(
        Object.keys(files).map(async (key) => {
          if (files[key]) {
            setItem(key);
            let path = `partners/${user.uid}/${key}`;
            if (typeof files[key] === "string") {
              const url = await uploadFile(path, files[key], setUploading);
              data[key] = url;
            } else {
              await Promise.all(
                Array.from(files[key]).map(async (file, idx) => {
                  const url = await uploadFile(
                    `${path}-${idx}`,
                    file,
                    setUploading
                  );
                  if (data[key]) data[key].push(url);
                  else data[key] = [url];
                })
              );
            }
          } else if (partner[key]) data[key] = partner[key];
          else data[key] = false;
        })
      );

      await requestPartnerAPI("submit-credit-app", data);
      fetchData(() => {
        navigate("/settings/credit-app/agreement");
        setEdit(false);
        setLoading(false);
      });
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  // =================================================== SIGN HANDLER
  const signHandler = async (signature) => {
    try {
      setLoading(true);
      const url = await uploadBase64String(
        `partners/${user.uid}/signature`,
        signature
      );
      await requestPartnerAPI("sign-credit-app", { signature: url });
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  return (
    <div className="bg">
      <Heading
        isBackArrow={true}
        title="Credit Application"
        description="The following information is collected and used to apply for vehicles loans on your behalf.
         You will be asked to sign a Power of Attorney and acknowledge disclaimers regarding how this information is used."
      >
        {!partner?.creditAppAgreement && (
          <div
            className="text-sm btn-warning animate-pulse"
            onClick={() => navigate("/settings/credit-app/agreement")}
          >
            Sign Agreement
          </div>
        )}
        {edit ? (
          <Fragment>
            <MdCheck className="icon" onClick={submitHandler} />
            <MdClose className="icon" onClick={() => setEdit(false)} />
          </Fragment>
        ) : (
          <FaEdit className="icon" onClick={() => setEdit(true)} />
        )}
      </Heading>
      <div className="grid-cols-4 info">
        <DetailPair label="Name" value={partner?.name} loading={loading} />
        <DetailPair
          label="Driver's License Number"
          value={partner.driversLicenseNumber}
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "driversLicenseNumber",
          }}
        />
        <DetailPair
          label="Phone"
          value={partner.phone}
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "phone",
          }}
        />
        <DetailPair
          label="Social Security Number"
          value="*** *** ****"
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "ssn",
            defaultValue: partner?.ssn || "",
          }}
        />
        <DetailPair
          label="Date of Birth"
          value={isoDateToString(partner.dob)}
        />
        <DetailPair label="Address" value={partner.address} />

        <div className="lg:col-span-4">
          <Heading title="Residence" />
        </div>

        <SelectPair
          label="Residence Type"
          value={partner.residenceType}
          options={["Own", "Rent", "Other"]}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "residenceType",
            defaultValue: partner?.residenceType || "Rent",
          }}
        />
        <DetailPair
          label="Monthly Rent/Mortgage"
          value={partner.monthlyRent}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "monthlyRent",
          }}
        />
        <DetailPair
          label="Years at Residence"
          value={partner.yearsAtResidence}
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "yearsAtResidence",
            type: "number",
          }}
        />
        <DetailPair
          label="Months at Residence"
          value={partner.monthsAtResidence}
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "monthsAtResidence",
            type: "number",
          }}
        />

        <div className="lg:col-span-4">
          <Heading title="Employment" />
        </div>

        <DetailPair
          label="Employer"
          value={partner.employer}
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "employer",
            defaultValue: partner?.employer || "",
          }}
        />
        <DetailPair
          label="Occupation"
          value={partner.occupation}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "occupation",
            defaultValue: partner?.occupation || "",
          }}
        />
        <DetailPair
          label="Years at Current Employment"
          value={partner.employmentYears}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "employmentYears",
            defaultValue: partner?.employmentYears || "",
          }}
        />
        <DetailPair
          label="Monthly Salary"
          value={partner.monthlySalary}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "monthlySalary",
            type: "number",
          }}
        />
        <DetailPair
          label="Other Income"
          value={partner.otherIncome}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "otherIncome",
            type: "number",
          }}
        />
        <div className="lg:col-span-4">
          <Heading title="Documents" />
        </div>
        <LinkPair
          label="Picture of SSN"
          link={partner.ssnFront}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "ssnFront",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Selfie with ID"
          link={partner.selfieId}
          edit={edit}
          loading={loading}
          inputOptions={{
            id: "selfieId",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Proof of Residence"
          link={partner.proofOfResidence}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "proofOfResidence",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Tax Returns (Last 2 years)"
          link={partner.taxReturns}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "taxReturns",
            multiple: true,
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Pay Stubs (Last 2)"
          link={partner.payStubs}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "payStubs",
            multiple: true,
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Bank Statements (Last 2 months)"
          link={partner.bankStatements}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "bankStatements",
            multiple: true,
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Credit Application Agreement"
          loading={loading}
          link={partner.creditAppAgreement}
        />
      </div>
      {uploading && (
        <p className="text-sm text-stone-400">
          Uploading {item}... {uploading}%
        </p>
      )}
    </div>
  );
}
